/**
 * Transport (hotel pickup) pricing — single source of truth for every surface
 * (api, web, admin, mobile). Pickup is an OPTIONAL add-on priced by group size.
 *
 * `transport` tri-state (per package): `null` = pickup not offered,
 * `0` = pickup included free, `> 0` = optional paid add-on at this price.
 * A negative value (the catalogue's legacy `-1`) also means not offered —
 * see `isTransportOffered`.
 * Tiered venues also set `transportLarge` (> 0) — the large-group price.
 *
 * Tiers (by paying guest count):
 *   - `2 .. LARGE_GROUP_MIN_GUESTS-1` (i.e. up to 7) → small `transport` price
 *   - `LARGE_GROUP_MIN_GUESTS .. LARGE_GROUP_MAX_GUESTS` (8..12) → `transportLarge`
 *   - `> LARGE_GROUP_MAX_GUESTS` (13+) → pickup WITHDRAWN (tiered venues only)
 *
 * Flat-pickup venues (no `transportLarge`) keep offering pickup at any group
 * size — the cutoff applies only when a large-group price is defined, so this
 * never regresses non-tiered activities.
 *
 * On top of the tier price, a package may define `transportOutside` — a flat
 * surcharge applied when the guest is staying outside the activity's own city
 * (Agafay camps charge +100 MAD to drive beyond Marrakech).
 */

/** Large-group transport price kicks in at this many paying guests. */
export const LARGE_GROUP_MIN_GUESTS = 8;

/** Above this many paying guests, a tiered venue stops offering pickup. */
export const LARGE_GROUP_MAX_GUESTS = 12;

/** Negative/undefined price = category not available; clamp to 0. Keeps 0 (free). */
function clampPrice(p: number | null | undefined): number {
  return typeof p === "number" && p > 0 ? p : 0;
}

/** True when a venue defines a large-group transport price (i.e. it is tiered). */
export function hasLargeTransportTier(large: number | null | undefined): boolean {
  return typeof large === "number" && large > 0;
}

/**
 * Whether this package offers pickup at all, independent of group size.
 * `null` = not offered; `0` (free) or `> 0` (paid) = offered.
 *
 * A negative price is also "not offered". The catalogue carries `-1` on
 * hundreds of packages — the sentinel the legacy admin wrote for "no pickup",
 * and `clampPrice` below has always read it that way. This check did not: it
 * asked only whether the value was a number, so `-1` counted as offered, was
 * clamped to `0`, and `0` means free. Every booking surface then drew a
 * "Hotel pickup — Included" toggle on activities whose own page says pickup
 * is not offered (owner, 2026-09-10, Quad Casablanca: "pick up is not
 * included, why it shows included??").
 */
export function isTransportOffered(small: number | null | undefined): boolean {
  return typeof small === "number" && small >= 0;
}

/**
 * Whether the pickup add-on can be selected for a group of `paidGuests`.
 * Tiered venues withdraw pickup above `LARGE_GROUP_MAX_GUESTS`; flat venues
 * offer it at any size.
 */
export function isTransportAvailable(
  small: number | null | undefined,
  large: number | null | undefined,
  paidGuests: number,
): boolean {
  if (!isTransportOffered(small)) return false;
  if (hasLargeTransportTier(large) && paidGuests > LARGE_GROUP_MAX_GUESTS) return false;
  return true;
}

/** True when a package charges extra to collect guests outside its own city. */
export function hasOutsideSurcharge(outside: number | null | undefined): boolean {
  return typeof outside === "number" && outside > 0;
}

/**
 * Where the guest is being picked up from, relative to the activity's city.
 * Agafay camps, for instance, add a flat surcharge to drive beyond Marrakech.
 */
export interface TransportLocationOptions {
  /** Guest is staying outside the activity's city. */
  outside?: boolean;
  /** The package's outside-city surcharge (MAD). `null`/0 = none. */
  outsideSurcharge?: number | null;
}

/**
 * Resolved pickup price for `paidGuests`. Returns `0` when pickup is unavailable
 * (not offered, or group above the cap on a tiered venue) so a stale
 * `transport = true` flag can never add a phantom charge — and so the
 * outside-city surcharge can never resurrect a withdrawn pickup.
 */
export function resolveTransportPrice(
  small: number | null | undefined,
  large: number | null | undefined,
  paidGuests: number,
  location: TransportLocationOptions = {},
): number {
  if (!isTransportAvailable(small, large, paidGuests)) return 0;
  const base =
    hasLargeTransportTier(large) && paidGuests >= LARGE_GROUP_MIN_GUESTS
      ? (large as number)
      : clampPrice(small);
  const surcharge =
    location.outside && hasOutsideSurcharge(location.outsideSurcharge)
      ? (location.outsideSurcharge as number)
      : 0;
  return base + surcharge;
}

/** Counts a reservation is priced from. hours/nights/groups only matter for their pricing type. */
export interface ReservationBasePriceInput {
  pricing: { type: 0 | 1 | 2 | 3; adult: number; child: number; infant: number };
  adults: number;
  children: number;
  infants: number;
  hours: number;
  nights: number;
  groups: number;
}

/** NaN-safe non-negative quantity (form inputs hand us "" -> NaN). */
function priceQty(value: number, min = 0): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, n);
}

/** -1 = that guest type is not sold (adds nothing), 0 = free, >0 = paid. */
function ratable(rate: number, count: number): number {
  if (rate === -1) return 0;
  const r = Number(rate);
  return Number.isFinite(r) ? r * count : 0;
}

/**
 * Package-rate price for a set of counts, before transport, add-ons and
 * transfer fees. typePrice: 0 per person, 1 per hour, 2 per night, 3 per
 * group (the adult rate prices one unit). Shared by the admin edit form and
 * the API's change-booking quote so both read the same rates the same way.
 */
export function reservationBasePrice(input: ReservationBasePriceInput): number {
  const { pricing } = input;
  switch (pricing.type) {
    case 1:
      return ratable(pricing.adult, priceQty(input.hours, 1));
    case 2:
      return ratable(pricing.adult, priceQty(input.nights, 1));
    case 3:
      return ratable(pricing.adult, priceQty(input.groups, 1));
    default:
      return (
        ratable(pricing.adult, priceQty(input.adults)) +
        ratable(pricing.child, priceQty(input.children)) +
        ratable(pricing.infant, priceQty(input.infants))
      );
  }
}
